class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /* Le tiroir garde le balisage rendu lors de sa derniere mise a jour. Si le
     panier a change ailleurs (page panier, autre onglet, widget de lots), les
     quantites affichees sont perimees : le bouton « + » renvoie alors
     ancienne+1 et le panier REDESCEND. On resynchronise donc avant d'agir. */
  async resyncSiPerime() {
    try {
      const panier = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      const champs = Array.from(this.querySelectorAll('.quantity__input'));
      const affiche = champs.reduce((t, i) => t + (parseInt(i.value, 10) || 0), 0);
      if (affiche === panier.item_count) return false;
      const sections = await (await fetch('/?sections=cart-drawer,cart-icon-bubble')).json();
      this.getSectionsToRender().forEach((section) => {
        const el = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (!el || !sections[section.id]) return;
        el.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
      });
      return true;
    } catch (e) {
      return false;
    }
  }

  surveillePerime() {
    if (this.dataset.gpSurveille) return;
    this.dataset.gpSurveille = '1';
    // tiroir deja ouvert : on verifie au retour sur l'onglet
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.classList.contains('active')) this.resyncSiPerime();
    });
    /* Un bouton de quantite agit sur la valeur affichee : si elle est perimee,
       le panier redescend. La verification etant asynchrone, on retient le clic,
       on remet le tiroir a jour, puis on rejoue le clic sur le bouton equivalent. */
    this.addEventListener('click', (e) => {
      const bouton = e.target.closest && e.target.closest('.quantity__button');
      if (!bouton) return;
      if (bouton.dataset.gpRejoue) { delete bouton.dataset.gpRejoue; return; }
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      const nom = bouton.getAttribute('name');
      const ligne = bouton.closest('[id^="CartDrawer-Item-"]');
      const index = ligne ? ligne.id.replace('CartDrawer-Item-', '') : '1';
      this.resyncSiPerime().then(() => {
        const cible =
          this.querySelector('#CartDrawer-Item-' + index + ' .quantity__button[name="' + nom + '"]') ||
          this.querySelector('.quantity__button[name="' + nom + '"]');
        if (!cible) return;
        cible.dataset.gpRejoue = '1';
        cible.click();
      });
    }, true);
  }

  open(triggeredBy) {
    this.resyncSiPerime();
    this.surveillePerime();
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
